import { forwardRef, useImperativeHandle } from "react";
import { GetGenericAccountsResponse } from "../../../api/services/client/models";
import {
  AccordionHeader,
  MuiBox,
  WarningConfirmationModal,
} from "../../../components";
import { AccordionHeaderText } from "../../../components/accordionHeader/AccordionHeaderText";
import { ExpandCollapseButton } from "../../../components/expandCollapseButton/ExpandCollapseButton";
import {
  mapDropdownDataWithName,
  mapSelectOptionToFirstIndex,
} from "../../../helpers/utils";
import { CreateGenericAccountBasicDetailsForm } from "../createGenericAccount/components/createGenericAccountBasicDetailsForm/CreateGenericAccountBasicDetailsForm";
import { CreateGenericAccountConfigurationForm } from "../createGenericAccount/components/createGenericAccountConfigurationForm/CreateGenericAccountConfigurationForm";
import { EditGenericAccountWrapper } from "./EditGenericAccount.style";
import { useEditGenericAccountHook } from "./useEditGenericAccount";

export interface EditGenericAccountProps {
  genericAccountData?: GetGenericAccountsResponse;
  onApiSuccess?: () => void;
  onApiError?: () => void;
}

export interface EditGenericAccountRefProps {
  triggerApi: () => void;
}

export const EditGenericAccount = forwardRef<
  EditGenericAccountRefProps,
  EditGenericAccountProps
>((props, ref) => {
  const {
    deliveryOfficeRef,
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
    // state
    showCancelConfirmationModal,
    showSaveUpdateConfirmationModal,
    // handlers
    handleCancelModalPrimaryButtonClick,
    handleCancelModalClose,
    handleSaveModalPrimaryButtonClick,
    handleUpdateClick,
    // data
    usersList,
    commonLabels,
    genericAccountBasicDetailsTitle,
    genericAccountConfigurationTitle,
    confirmationToCreateGenericAccountLabel,
  } = useEditGenericAccountHook(props);

  useImperativeHandle(ref, () => ({
    triggerApi: handleUpdateClick,
  }));

  const { genericAccountData } = props;

  const renderHeader = (label: string) => <AccordionHeaderText label={label} />;
  const renderSeparator = () => <MuiBox className="separator-space" />;

  const renderBasicDetailsAccordion = () => {
    return (
      <AccordionHeader
        title={renderHeader(genericAccountBasicDetailsTitle)}
        onChange={() => handleIndividualAccordionChange("basicDetails")}
        isExpanded={accordionStatus.basicDetails}
      >
        <CreateGenericAccountBasicDetailsForm
          isEditGenericAccount={true}
          usersList={mapSelectOptionToFirstIndex(
            mapDropdownDataWithName(usersList, true),
            commonLabels.selectPlaceHolder,
          )}
        />
      </AccordionHeader>
    );
  };

  const renderConfigurationAccordion = () => {
    return (
      <AccordionHeader
        title={renderHeader(genericAccountConfigurationTitle)}
        onChange={() => handleIndividualAccordionChange("configuration")}
        isExpanded={accordionStatus.configuration}
      >
        <CreateGenericAccountConfigurationForm
          isEditGenericAccount={true}
          deliveryOfficeRef={deliveryOfficeRef}
          genericAccountData={genericAccountData}
        />
      </AccordionHeader>
    );
  };

  return (
    <EditGenericAccountWrapper>
      <ExpandCollapseButton
        accordionStatus={accordionStatus}
        handleExpandCollapseAllChange={handleExpandCollapseAllChange}
      />

      {renderSeparator()}

      {renderBasicDetailsAccordion()}

      {renderSeparator()}

      {renderConfigurationAccordion()}

      {renderSeparator()}

      {showSaveUpdateConfirmationModal && (
        <WarningConfirmationModal
          labelContent={confirmationToCreateGenericAccountLabel}
          show={showSaveUpdateConfirmationModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleCancelModalClose}
          onPrimaryButtonClick={handleSaveModalPrimaryButtonClick}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}

      {showCancelConfirmationModal && (
        <WarningConfirmationModal
          labelContent={commonLabels.cancelButtonActionText}
          show={showCancelConfirmationModal}
          onClose={handleCancelModalClose}
          onPrimaryButtonClick={handleCancelModalPrimaryButtonClick}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </EditGenericAccountWrapper>
  );
});
